// Microbe v2 — shared TS contract. Idle-economy core, ONE persistent living world.
// Every /microbe module builds against these types. Kit types are imported type-only.
//
// v2 vs v1: no dives/timer. Currencies are earned IN-world (eating) + idle. The spec tree is a
// data-driven "genome" map (a Partial<Record<TraitId, number>>) so new traits (resistances,
// reproduction) slot in later as new ids with ZERO migration — a genome predating a trait just
// reads level 0 through lvl(). Mass grows from feeding and drives size + camera zoom.
import type { SaveStore, AudioEngine } from '../src/index.js';

/** Bump when the persisted shape changes in a way migrate.ts must handle. */
export const SAVE_VERSION = 2;

/** All levelable traits = one genome. MVP ships the v1 six; resistances/reproduction traits are
 *  added later as new ids (default-0 accessor means no migration for additive traits). */
export type TraitId =
  | 'photosynthesis'
  | 'co2Harvesting'
  | 'motility'
  | 'membrane'
  | 'o2Tolerance'
  | 'fermentation';

export type Branch = 'metabolism' | 'defense' | 'motility';

/** The four currencies. energy+biomass are idle-funded AND fed by eating, storage-capped.
 *  xp+enzymes are earned only by acting in the world (eating), uncapped; enzymes are rare and
 *  gate the deeper (`rare`) traits — this is where the idle layer and the active layer couple. */
export interface Wallet {
  energy: number;
  biomass: number;
  xp: number;
  enzymes: number;
}
export type CostKey = keyof Wallet;
export type Cost = Partial<Wallet>;

/** Owned trait levels; an ABSENT key means level 0. Data-driven for cheap extension. */
export type Genome = Partial<Record<TraitId, number>>;

/** Persisted save (localStorage key 'gamekit_microbe', version SAVE_VERSION). Compact digest. */
export interface MicrobeState {
  version: number;
  wallet: Wallet;
  genome: Genome;
  /** Organism mass — grows from feeding; drives on-screen size, camera zoom, division-readiness. */
  mass: number;
  /** Epoch ms of the last foreground stamp — drives dormancy / offline catch-up. */
  lastSeen: number;
  soundOn: boolean;
  /** Lifetime stats (cosmetic now; a prestige input later). */
  stats: { maxMass: number; totalEaten: number; born: number };
}

/** Static trait metadata + cost curve (the data table lives in state.ts). */
export interface TraitDef {
  id: TraitId;
  name: string;
  icon: string; // emoji
  blurb: string;
  branch: Branch;
  max: number;
  /** Rare traits cost enzymes (the scarce, actively-earned currency). */
  rare: boolean;
  /** Flagged evolutionary dead-end (fermentation). Boolean only; GOE is NOT built. */
  deadEnd: boolean;
  /** Cost to go from `level` -> `level + 1`. Only the currencies that apply are present. */
  cost(level: number): Cost;
}

/** Derived per-second production + storage cap, from the current genome. */
export interface Rates {
  energy: number;
  biomass: number;
  cap: number; // storage cap applied to energy + biomass (xp/enzymes never capped)
}

/** Dormancy / offline catch-up report (for the "while you were away" beat). */
export interface Catchup {
  energy: number;
  biomass: number;
  seconds: number;
}

/**
 * The single surface the world (canvas) + genome popup + HUD all share. All state access goes
 * through here so the renderers never touch the save directly.
 */
export interface GameCtx {
  save: SaveStore<MicrobeState>;
  audio: AudioEngine;
  haptic(pattern: number | number[]): void;

  // --- reads ---
  state(): Readonly<MicrobeState>;
  lvl(id: TraitId): number; // genome level, default 0
  rates(): Rates; // derived production
  wallet(): Readonly<Wallet>;

  // --- economy writes (persisted + clamped) ---
  credit(delta: Partial<Wallet>): void; // add currency (energy/biomass capped; xp/enzymes not)
  addMass(dm: number): void; // feeding grows mass; tracks stats.maxMass + totalEaten
  canBuy(id: TraitId): boolean;
  buyTrait(id: TraitId): boolean; // spend + level up; returns false if maxed/unaffordable

  // --- world / UI plumbing ---
  startPause(): void; // world soft-pauses while the genome popup is open
  endPause(): void;
  isPaused(): boolean;
  sfx(name: string): void; // play a named sound (no-op if disabled)
  /** Subscribe HUD/popup to state changes; returns an unsubscribe fn. */
  onChange(fn: () => void): () => void;
}
